import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Grid,
  Link,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import Alert from '@material-ui/lab/Alert';

import {
  useHistory,
  withRouter,
} from 'react-router-dom';

import SpinnerLoading from '../feedback/spinnerLoading';
import TranslationWarning from '../modals/translationWarning';

import { getAccess, getNewPassword } from '../../utils/comms';

import translations from '../../languages/i18n';
import { addInternalCommonContents }  from '../../languages/i18n';

import { loginForm } from '../../styles/formElementsStyles';

import { SessionContext } from '../../utils/context';

import { timeUntilNewAttempt } from '../../utils/constants_comms';

// NOTA: Per poder emprar el react-router-dom i enllaçar pàgines s'ha de convertir el component en una funció i exportar-lo embolicat per withRouter
// En cas de que el component sigui un fill directe de Route no fa falta fer-ho
// Explicat al punt 2.1. Using withRouter de https://dev.to/projectescape/programmatic-navigation-in-react-3p1l

function Login() {
  const sessionContext = useContext(SessionContext);

  const loginStyle = loginForm();

  const [username, setUsername] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showTranslationWarning, setShowTranslationWarning] = useState(false);
  const [responseLogin, setResponseLogin] = useState('');
  const [severityMessage, setSeverityMessage] = useState('');

  const history = useHistory();

  const tooManyAttemptsText = `${translations['_errorTooManyAttemptsSpecific1']} ${timeUntilNewAttempt} ${translations['_errorTooManyAttemptsSpecific2']}`;
    
  const handleCloseDialog = () => {
    setShowTranslationWarning(false);
    history.push(userHome(sessionContext.role));
  };

  // Segons el rol de l'usuari començam a una pàgina o altra
  const userHome = (userRole) => {
    switch (userRole) {
      case 'Admin':
        return `/${userRole.toLowerCase()}/listClinicians`;
      case 'Clinician':
        return `/${userRole.toLowerCase()}/listPatients`;
      case 'Patient':
        return `/${userRole.toLowerCase()}/listPatientForms`;
      // Cas especial per als tècnics dels hospitals  
      case 'Program_Configuration_User':
        return `/technical/listProgramsTechnical`;
      default:
        return '/';
    }
  };

  useEffect(() => {
    const getSessionInfoAndInternalContents = async () => {
      let initialRoute = '/';
      const sessionInfo = sessionStorage.getItem('sessionInfo');
      // si existeix miram si el token es valid
      if (sessionInfo) {
        // el storage del navegador només treballa amb string, ho convertesc en un objecte JSON per poder manipular-lo
        const sessionInfoObj = JSON.parse(sessionInfo);
        // consideram que el token ja no és vàlid si caduca d'aquí a 5 segons, per evitar que pugui caducar
        // en el moment de fer la consulta a la BDD per sol.licitar les dades per carregar la següent pàgina
        const isValid = sessionInfoObj.tokenExp * 1000 > (Date.now() + 5000);
        if (isValid) {
          // Guardam al contexte la informació de la sessió que necessitarem al resta de l'app
          sessionContext.username = sessionInfoObj.username;
          sessionContext.userId = sessionInfoObj.userId;
          sessionContext.role = sessionInfoObj.role;
          sessionContext.userToken = sessionInfoObj.userToken;
          sessionContext.tokenExp = sessionInfoObj.tokenExp;

          // Obtenim de nou els textos interns (si passam per aquí és perquè s'ha recarregat la pàgina i, per tant, ja no disposam d'aquests textos)
          let internalTranslationsAvailable = true;
          try {
            await addInternalCommonContents(sessionContext.userToken);
          } catch (error) {
            internalTranslationsAvailable = false;        
            setShowTranslationWarning(true);
          }

          if (internalTranslationsAvailable) {
            initialRoute = userHome(sessionContext.role);
            history.push(initialRoute);
          } 
        }
      }
    };
    getSessionInfoAndInternalContents();    
    // history.push(initialRoute);
  }, []); // [] Indica que l'useEffect només s'executa una vegada, no a cada render
  
  const manageErrorResponse = (response) => {
    let messageError = '';
    if (response === 'timeOut') { // El servidor no respon
      messageError = translations['_errorTimeOut'];
    } else { // Per a qualsevol altre resposta mostram el missatge genèric d'error
      messageError = translations['_errorGeneric'];
    }
    return messageError;
  };

  const forgotPassword = async () => {
    if (username.length === 0) {
      setSeverityMessage('error');
      setResponseLogin(translations['_userNameRequired']);
    } else {
      setShowLoading(true);
      const response = await getNewPassword(username);
      setShowLoading(false);
      if (response.status === 200) { // Solicitud realitzada correctament
        // setUsername('');
        setSeverityMessage('success');
        setResponseLogin(translations['_seeEmail']);
      } else if (response === 429) { // Massa reintents
        setSeverityMessage('error');
        setResponseLogin(tooManyAttemptsText);      
      } else if (response === 'timeOut') { // El servidor no respon
        setSeverityMessage('error');
        setResponseLogin(manageErrorResponse(response));
      } else { // Resta d'errors
        setSeverityMessage('error');
        setResponseLogin(translations['_wrongOperation']);
      }
    }    
  };

  const onChangeTextUsername = (input) => {
    setUsername(input.target.value);
  };

  const onChangeTextUserPassword = (input) => {
    setUserPassword(input.target.value);
  };

  const onChangePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLogin = async () => {
    setShowLoading(true);
    const response = await getAccess(username, userPassword);
    setShowLoading(false);
    if (response.status === 200) { // Login OK
      // setUsername('');
      // Guardam al contexte la informació de la sessió que necessitarem al resta de l'app
      sessionContext.username = username;
      sessionContext.userId = response.data.token.userId;
      sessionContext.role = response.data.token.role;
      sessionContext.userToken = response.data.token.token;
      sessionContext.tokenExp = response.data.token.exp;
      // Activam el recordatori, per si l'usuari no ha tancat la pestanya i anteriorment li ha dit que no vol que li recordin més
      sessionContext.rememberTokenIsNearlyToExpire = true;
      // Guardam al storage del navegador les dades de la sessió
      sessionStorage.setItem('sessionInfo', JSON.stringify({
        username,
        userId: response.data.token.userId,
        role: response.data.token.role,
        userToken: response.data.token.token,
        tokenExp: response.data.token.exp,
      }));
      
      // Obtenim els textos interns
      let internalTranslationsAvailable = true;
      try {
        await addInternalCommonContents(response.data.token.token);        
      } catch (error) {
        internalTranslationsAvailable = false;        
        setShowTranslationWarning(true);
      }

      if (internalTranslationsAvailable) {
        history.push(userHome(response.data.token.role));
      } 

    } else if (response === 400) { // Login KO. Nom de usuari o contrasenya incorrectes
      setSeverityMessage('error');
      setResponseLogin(translations['_errorLogin']);
    } else if (response === 429) { // Login KO. Massa reintents
      setSeverityMessage('error');
      setResponseLogin(tooManyAttemptsText);      
    } else { // Resta d'errors
      setSeverityMessage('error');
      setResponseLogin(manageErrorResponse(response));
    }
  };

  return (
    <React.Fragment>
      <Container component='main' maxWidth='xs'>
        <SpinnerLoading show={showLoading} />
        <TranslationWarning handleClose={handleCloseDialog} message={translations['_errorInternalCommonTranslationsNotAvailable']} open={showTranslationWarning} title={translations['_alert']} />
        <div className={loginStyle.content}>
          <Grid container direction='row' spacing={2} alignItems='center' alignContent='center'>
            <Grid item xs={12}>
              <Grid container direction='column' spacing={3}>
                <Grid item xs={12}>
                  <Grid container direction='column' spacing={1} alignItems='center'>
                    <Grid item>
                      <img src={require('../../assets/images/logoHT.png')} alt='HT Logo' width='300' heigth='300' />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    variant='outlined'
                    required
                    fullWidth
                    error={username.length === 0 || false}
                    id='username'
                    label={translations['_username']}
                    name='username'
                    autoComplete='username'
                    autoFocus
                    value={username}
                    onChange={onChangeTextUsername}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    variant='outlined'
                    required
                    fullWidth
                    error={userPassword.length === 0 || false}
                    type={showPassword ? 'text' : 'password'}
                    id='userPassword'
                    label={translations['_userPassword']}
                    name='password'
                    autoComplete='current-password'
                    value={userPassword}
                    onChange={onChangeTextUserPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={onChangePasswordVisibility}
                            onMouseDown={onMouseDownPassword}
                            edge='end'
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  {responseLogin ? <Alert severity={severityMessage}>{responseLogin}</Alert> : '' }
                  <Button
                    fullWidth
                    variant='contained'
                    className={loginStyle.loginButton}
                    onClick={handleLogin}
                    size='large'
                  >
                    {translations['_signIn']}
                  </Button>
                </Grid>
                <Grid item>
                  <Link href='#' variant='body1' onClick={forgotPassword}>{translations['_forgotPassword']}</Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Container>

      <Container component='div' maxWidth='lg'>
        <Grid container direction='row' justify='center' alignItems='center'>
          <Grid item className={loginStyle.containerLogoItem}>
            <Typography className={loginStyle.typography1}>
              {translations['_developedBy']}
            </Typography>
            <div>
              <Grid container direction='row' alignItems='flex-start' >
                <Grid item>
                  <img src={require('../../assets/images/logo_HUSE.png')} alt='Logo HUSE' width='200' />
                </Grid>
                <Grid item>
                  <img src={require('../../assets/images/logo_FBIT.png')} alt='Logo FBIT' width='200' />
                </Grid>
              </Grid>
            </div>
          </Grid>
          <Grid item className={loginStyle.containerLogoItem}>
            <Typography className={loginStyle.typography2}>
              {translations['_compatibleWith']}
            </Typography>
            <img src={require('../../assets/images/logo_ICHOM.png')} alt='Logo ICHOM' width='200' />
          </Grid>
        </Grid>
      </Container>
    </React.Fragment>
  );
}

export default withRouter(Login);
